// FunctionalSpecification.js — Documentation Fonctionnelle : POC « Domaines d'audit » (module Paramétrage).
// Réplique INTERACTIVE et fidèle de l'écran réel de gestion des domaines CBS : liste paginée + filtres
// (statut / type de système / environnement), détail à onglets Informations / Modules CBS (référentiel
// AuditModule : origine DECLARE/DECOUVERT, retrait = désactivation), formulaire création/édition et
// activation/désactivation — DOUBLÉE d'un guide utilisateur pas-à-pas. Actions SIMULÉES localement (démo).
// Source de vérité : scanbank-frontend « [SCANBANK] Parametrage/[PRM][01]-Domaines.js » +
// scanbank_core AuditScanBankController (/api/scanbank/audit/domains) + AuditModuleController (/audit/modules).
import React, { useState } from 'react';
import {
  Plus, Edit, X, Save, Power, PowerOff, CheckCircle, XCircle, Search, Info, ArrowRight,
  Database, Server, Globe, Users, User, CalendarDays, Layers, Eye, ChevronLeft, ChevronRight, Tag,
} from 'lucide-react';
import { Card, CardContent, Button, Badge, Tabs } from '../ui';

// Types de système proposés par l'écran réel (valeurs exactes du select)
const SYSTEM_TYPES = ['CBS', 'CORE_BANKING', 'ERP', 'RISK_MANAGEMENT', 'AML', 'OTHER'];
// Environnements proposés par l'écran réel
const ENVIRONMENTS = ['PROD', 'UAT', 'DEV', 'PREPROD', 'DR'];
const ENV_COLORS = { PROD: '#10B981', UAT: '#EAB308', PREPROD: '#F97316', DEV: '#6B7280', DR: '#8B5CF6' };
// Origine d'un module CBS (enum backend ModuleOrigine)
const ORIGINES = {
  DECLARE: { label: 'Déclaré', color: '#6366F1' },
  DECOUVERT: { label: 'Découvert', color: '#F97316' },
};

// Échantillon réaliste : le CBS Amplitude (BMCI) audité par le générateur GENERO + recette UAT.
const SAMPLE = [
  {
    code: 'CBS_AMPLITUDE', name: 'Core Banking Amplitude — BMCI', systemType: 'CBS', version: 'v11',
    environment: 'PROD', owner: 'a.elfassi', isActive: true,
    description: 'CBS Sopra Amplitude v11 — périmètre production audité quotidiennement via les exports XML GENERO.',
    createdBy: 'admin', createdDate: '2026-05-02', modifiedBy: 'a.elfassi', modifiedDate: '2026-07-10',
    moduleDetails: [
      { code: 'COMPTABILITE', libelle: 'Comptabilité générale', responsable: 'm.benali', isActive: true, origine: 'DECLARE' },
      { code: 'CREDITS', libelle: 'Gestion des crédits', responsable: 's.ouazzani', isActive: true, origine: 'DECLARE' },
      { code: 'CLIENTS', libelle: 'Référentiel clients', responsable: 'k.idrissi', isActive: true, origine: 'DECLARE' },
      { code: 'DEPOTS', libelle: 'Dépôts et épargne', responsable: null, isActive: true, origine: 'DECLARE' },
      { code: 'VIREMENTS', libelle: 'Virements et transferts', responsable: 'n.tazi', isActive: true, origine: 'DECLARE' },
      { code: 'GUICHET', libelle: 'Opérations de guichet', responsable: null, isActive: true, origine: 'DECOUVERT' },
      { code: 'TRESORERIE', libelle: 'Trésorerie', responsable: null, isActive: false, origine: 'DECLARE' },
    ],
  },
  {
    code: 'CBS_AMPLITUDE_UAT', name: 'Amplitude — Recette BMCI', systemType: 'CBS', version: 'v11',
    environment: 'UAT', owner: 'a.elfassi', isActive: true,
    description: 'Environnement de recette Amplitude : validation des nouveaux contrôles avant passage en production.',
    createdBy: 'admin', createdDate: '2026-05-02', modifiedBy: 'admin', modifiedDate: '2026-06-18',
    moduleDetails: [
      { code: 'COMPTABILITE', libelle: 'Comptabilité générale', responsable: 'm.benali', isActive: true, origine: 'DECLARE' },
      { code: 'HABILITATION', libelle: 'Habilitations et profils', responsable: 'y.raji', isActive: true, origine: 'DECLARE' },
    ],
  },
  {
    code: 'CBS_BANK_CEMAC', name: 'Core Banking CEMAC', systemType: 'CORE_BANKING', version: 'v2.5.1',
    environment: 'PROD', owner: 'j.mbarga', isActive: true,
    description: 'CBS des filiales CEMAC — conformité GAFI/BAM.',
    createdBy: 'admin', createdDate: '2026-06-01', modifiedBy: 'admin', modifiedDate: '2026-06-01',
    moduleDetails: [
      { code: 'HABILITATION', libelle: 'Habilitations', responsable: null, isActive: true, origine: 'DECLARE' },
      { code: 'COMPTABILITE', libelle: 'Comptabilité', responsable: null, isActive: true, origine: 'DECOUVERT' },
    ],
  },
  {
    code: 'ERP_SAGE_RH', name: 'ERP Sage — Paie & RH', systemType: 'ERP', version: '2024',
    environment: 'DEV', owner: null, isActive: false,
    description: 'Périmètre exploratoire — non audité pour le moment.',
    createdBy: 'ops', createdDate: '2026-06-20', modifiedBy: 'ops', modifiedDate: '2026-06-25',
    moduleDetails: [],
  },
];

const EMPTY_FORM = { code: '', name: '', description: '', systemType: 'CBS', version: '', environment: 'PROD', owner: '', isActive: true, moduleDetails: [] };
const PAGE_SIZE = 3;

// ---- Petits sous-composants de la réplique ---------------------------------
const ActifBadge = ({ active }) => active
  ? <Badge color="#10B981"><CheckCircle className="h-3 w-3" />Actif</Badge>
  : <Badge><XCircle className="h-3 w-3" />Inactif</Badge>;

const StatCard = ({ icon: IconCmp, label, value }) => (
  <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50">
    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-1"><IconCmp className="h-4 w-4" />{label}</div>
    <div className="text-sm font-medium text-gray-900 dark:text-gray-100 break-all">{value || 'N/A'}</div>
  </div>
);

const GuideStep = ({ n, children }) => (
  <li className="flex items-start gap-3">
    <span className="shrink-0 w-6 h-6 rounded-full bg-indigo-500 text-white text-xs font-bold flex items-center justify-center">{n}</span>
    <span className="text-sm text-gray-700 dark:text-gray-300">{children}</span>
  </li>
);

const inputCls = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none';

// ---- Page -------------------------------------------------------------------
const DomainesPOC = () => {
  const [domains, setDomains] = useState(SAMPLE);
  const [selectedCode, setSelectedCode] = useState(SAMPLE[0].code);
  const [mode, setMode] = useState('detail'); // 'detail' | 'create' | 'edit'
  const [tab, setTab] = useState('info'); // 'info' | 'modules'
  const [search, setSearch] = useState('');
  const [filterActive, setFilterActive] = useState('all');
  const [filterEnv, setFilterEnv] = useState('all');
  const [page, setPage] = useState(0);
  const [form, setForm] = useState(EMPTY_FORM);
  const [newModuleCode, setNewModuleCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  const selected = domains.find((d) => d.code === selectedCode) || null;
  const filtered = domains.filter((d) =>
    (filterActive === 'all' || (filterActive === 'active' ? d.isActive : !d.isActive)) &&
    (filterEnv === 'all' || d.environment === filterEnv) &&
    (`${d.code} ${d.name} ${d.systemType}`.toLowerCase().includes(search.toLowerCase()))
  );
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const flash = (msg) => { setNotice(msg); window.setTimeout(() => setNotice(null), 2500); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e && e.target ? e.target.value : e }));

  const openCreate = () => { setForm(EMPTY_FORM); setNewModuleCode(''); setMode('create'); };
  const openEdit = (d) => { setSelectedCode(d.code); setForm({ ...d, moduleDetails: (d.moduleDetails || []).map((m) => ({ ...m })) }); setNewModuleCode(''); setMode('edit'); };
  const selectRow = (d) => { setSelectedCode(d.code); setMode('detail'); setTab('info'); };

  const formValide = form.code && /^[A-Z0-9_]+$/.test(form.code) && form.name && form.moduleDetails.filter((m) => m.isActive).length > 0;

  // Enregistrement simulé (le backend fait l'upsert des modules : nouveau code -> DECLARE actif,
  // code retiré de la liste envoyée -> module DÉSACTIVÉ, jamais supprimé)
  const submitForm = () => {
    if (!formValide || saving) return;
    setSaving(true);
    window.setTimeout(() => {
      if (mode === 'create') {
        const created = { ...form, createdBy: 'vous', createdDate: '2026-07-12', modifiedBy: 'vous', modifiedDate: '2026-07-12' };
        setDomains((prev) => [...prev, created]);
        setSelectedCode(form.code);
        flash(`Domaine « ${form.code} » créé avec succès`);
      } else {
        setDomains((prev) => prev.map((d) => (d.code === form.code ? { ...d, ...form, modifiedBy: 'vous', modifiedDate: '2026-07-12' } : d)));
        flash(`Domaine « ${form.code} » mis à jour avec succès`);
      }
      setSaving(false); setMode('detail'); setTab('modules');
    }, 500);
  };

  const toggleDomainActive = (d) => {
    setDomains((prev) => prev.map((x) => (x.code === d.code ? { ...x, isActive: !x.isActive } : x)));
    flash(d.isActive ? `Domaine « ${d.code} » désactivé` : `Domaine « ${d.code} » activé`);
  };

  // Ajout d'un module au formulaire : code normalisé trim().toUpperCase() (même règle que le backend).
  const ajouterModule = () => {
    const code = (newModuleCode || '').trim().toUpperCase();
    if (!code) return;
    setNewModuleCode('');
    setForm((f) => {
      const existant = f.moduleDetails.find((m) => m.code === code);
      if (existant) {
        // Code déjà connu (éventuellement désactivé) : on le réactive au lieu de le dupliquer
        return { ...f, moduleDetails: f.moduleDetails.map((m) => (m.code === code ? { ...m, isActive: true } : m)) };
      }
      return { ...f, moduleDetails: [...f.moduleDetails, { code, libelle: '', responsable: null, isActive: true, origine: 'DECLARE' }] };
    });
  };

  // Retrait : en édition le module est DÉSACTIVÉ (l'historique d'audit est conservé) ;
  // en création (rien de persisté) la ligne est simplement enlevée de la liste.
  const retirerModule = (code) => {
    setForm((f) => ({
      ...f,
      moduleDetails: mode === 'create'
        ? f.moduleDetails.filter((m) => m.code !== code)
        : f.moduleDetails.map((m) => (m.code === code ? { ...m, isActive: false } : m)),
    }));
  };

  const setResponsable = (code, val) => setForm((f) => ({
    ...f, moduleDetails: f.moduleDetails.map((m) => (m.code === code ? { ...m, responsable: val || null } : m)),
  }));

  return (
    <div className="space-y-8">
      {/* Intro */}
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Documentation Fonctionnelle — Domaines d'audit</h1>
          <Badge color="#6366F1">Module Paramétrage</Badge>
          <Badge color="#F59E0B">POC interactif</Badge>
        </div>
        <p className="mt-2 text-gray-600 dark:text-gray-300">Aperçu interactif de l'écran <strong>Domaines d'Audit</strong> : liste paginée, filtres, détail avec modules CBS, création/édition et activation. <span className="text-gray-500 dark:text-gray-400">Les actions ci-dessous sont simulées localement (démo) — aucun appel réseau.</span></p>
      </div>

      {/* ===== Réplique interactive de l'IHM ===== */}
      <Card className="overflow-hidden">
        <div className="px-5 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Database className="h-6 w-6" />
            <div>
              <h2 className="text-lg font-bold leading-tight">Domaines d'Audit</h2>
              <p className="text-xs text-white/80">Configuration des systèmes CBS à auditer — {domains.length} domaine(s)</p>
            </div>
          </div>
          <Button onClick={openCreate} className="bg-white/20 hover:bg-white/30 text-white"><Plus className="h-4 w-4" />Nouveau Domaine</Button>
        </div>

        {notice && (
          <div className="mx-4 mt-3 flex items-center gap-2 p-2 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-300 text-sm">
            <CheckCircle className="h-4 w-4 shrink-0" />{notice}
          </div>
        )}

        {/* Filtres */}
        <div className="p-4 flex flex-wrap items-center gap-3 border-b border-gray-200 dark:border-gray-700">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} placeholder="Rechercher un domaine..." className={`${inputCls} pl-10`} />
          </div>
          <select value={filterActive} onChange={(e) => { setFilterActive(e.target.value); setPage(0); }} className={`${inputCls} w-auto`}>
            <option value="all">Tous les statuts</option>
            <option value="active">Actifs uniquement</option>
            <option value="inactive">Inactifs uniquement</option>
          </select>
          <select value={filterEnv} onChange={(e) => { setFilterEnv(e.target.value); setPage(0); }} className={`${inputCls} w-auto`}>
            <option value="all">Tous les environnements</option>
            {ENVIRONMENTS.map((env) => <option key={env} value={env}>{env}</option>)}
          </select>
        </div>

        {/* Liste paginée */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
              <tr>{['Code', 'Nom', 'Type', 'Environnement', 'Modules', 'Statut', 'Actions'].map((h) => <th key={h} className="text-left font-semibold px-4 py-2 whitespace-nowrap">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-200">
              {pageRows.map((d) => {
                const modulesActifs = (d.moduleDetails || []).filter((m) => m.isActive);
                const on = selected?.code === d.code && mode !== 'create';
                return (
                  <tr key={d.code} onClick={() => selectRow(d)} className={`cursor-pointer transition-colors ${on ? 'bg-indigo-50 dark:bg-indigo-500/10' : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'}`}>
                    <td className="px-4 py-2.5">
                      <span className="flex items-center gap-2">
                        <span className={`inline-block h-2 w-2 rounded-full ${d.isActive ? 'bg-emerald-400' : 'bg-gray-400'}`} />
                        <span className="font-mono font-medium text-gray-900 dark:text-white">{d.code}</span>
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-gray-900 dark:text-white">{d.name}</div>
                      <div className="text-xs text-gray-500 truncate max-w-[240px]">{d.description}</div>
                    </td>
                    <td className="px-4 py-2.5"><Badge color={d.systemType === 'CBS' ? '#3B82F6' : '#8B5CF6'}>{d.systemType}</Badge></td>
                    <td className="px-4 py-2.5"><Badge color={ENV_COLORS[d.environment]}>{d.environment}</Badge></td>
                    <td className="px-4 py-2.5">
                      <span className="flex flex-wrap gap-1">
                        {modulesActifs.slice(0, 2).map((m) => <Badge key={m.code} color="#6366F1">{m.code}</Badge>)}
                        {modulesActifs.length > 2 && <Badge>+{modulesActifs.length - 2}</Badge>}
                        {modulesActifs.length === 0 && <span className="text-xs text-gray-400">—</span>}
                      </span>
                    </td>
                    <td className="px-4 py-2.5"><ActifBadge active={d.isActive} /></td>
                    <td className="px-4 py-2.5">
                      <span className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => selectRow(d)} title="Voir détails" className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"><Eye className="h-4 w-4" /></button>
                        <button onClick={() => openEdit(d)} title="Modifier" className="p-1.5 rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-gray-100 dark:hover:bg-gray-700"><Edit className="h-4 w-4" /></button>
                        {d.isActive
                          ? <button onClick={() => toggleDomainActive(d)} title="Désactiver" className="p-1.5 rounded-lg text-gray-400 hover:text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-500/10"><PowerOff className="h-4 w-4" /></button>
                          : <button onClick={() => toggleDomainActive(d)} title="Activer" className="p-1.5 rounded-lg text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-500/10"><Power className="h-4 w-4" /></button>}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {pageRows.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">Aucun domaine d'audit ne correspond aux critères</td></tr>}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
          <span>{filtered.length} domaine(s) — page {page + 1} / {totalPages}</span>
          <span className="flex items-center gap-1">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}><ChevronLeft className="h-4 w-4" />Précédent</Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>Suivant<ChevronRight className="h-4 w-4" /></Button>
          </span>
        </div>

        {/* Détail / Formulaire */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-5">
          {mode === 'create' || mode === 'edit' ? (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{mode === 'create' ? 'Nouveau Domaine d\'Audit' : `Modifier : ${form.code}`}</h3>
                <button onClick={() => setMode('detail')} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="text-sm">
                  <span className="block text-gray-700 dark:text-gray-300 mb-1">Code <span className="text-red-500">*</span></span>
                  <input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))} disabled={mode === 'edit'} placeholder="CBS_BANK_CEMAC" className={`${inputCls} font-mono ${mode === 'edit' ? 'opacity-60 cursor-not-allowed' : ''}`} />
                  {form.code && !/^[A-Z0-9_]+$/.test(form.code) && <span className="text-xs text-red-500">Majuscules, chiffres et underscores uniquement</span>}
                </label>
                <label className="text-sm">
                  <span className="block text-gray-700 dark:text-gray-300 mb-1">Nom <span className="text-red-500">*</span></span>
                  <input value={form.name} onChange={set('name')} placeholder="Core Banking System CEMAC" className={inputCls} />
                </label>
                <label className="text-sm">
                  <span className="block text-gray-700 dark:text-gray-300 mb-1">Type de Système <span className="text-red-500">*</span></span>
                  <select value={form.systemType} onChange={set('systemType')} className={inputCls}>
                    {SYSTEM_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </label>
                <label className="text-sm">
                  <span className="block text-gray-700 dark:text-gray-300 mb-1">Environnement <span className="text-red-500">*</span></span>
                  <select value={form.environment} onChange={set('environment')} className={inputCls}>
                    {ENVIRONMENTS.map((env) => <option key={env} value={env}>{env}</option>)}
                  </select>
                </label>
                <label className="text-sm">
                  <span className="block text-gray-700 dark:text-gray-300 mb-1">Version</span>
                  <input value={form.version} onChange={set('version')} placeholder="v2.5.1" className={inputCls} />
                </label>
                <label className="text-sm">
                  <span className="block text-gray-700 dark:text-gray-300 mb-1">Responsable</span>
                  <input value={form.owner || ''} onChange={set('owner')} placeholder="Responsable du domaine (username)" className={inputCls} />
                </label>
                <label className="text-sm sm:col-span-2">
                  <span className="block text-gray-700 dark:text-gray-300 mb-1">Description</span>
                  <textarea value={form.description} onChange={set('description')} rows={2} placeholder="Description du domaine d'audit..." className={inputCls} />
                </label>
              </div>

              {/* Modules à auditer */}
              <div className="mt-4">
                <span className="block text-sm text-gray-700 dark:text-gray-300 mb-2">Modules à auditer <span className="text-red-500">*</span></span>
                <div className="flex items-center gap-2 mb-3">
                  <input value={newModuleCode} onChange={(e) => setNewModuleCode(e.target.value.toUpperCase())}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); ajouterModule(); } }}
                    placeholder="Code du module (ex. HABILITATION)" className={`${inputCls} font-mono flex-1`} />
                  <Button onClick={ajouterModule} disabled={!newModuleCode.trim()}><Plus className="h-4 w-4" />Ajouter</Button>
                </div>
                <div className="space-y-2">
                  {form.moduleDetails.map((m) => (
                    <div key={m.code} className={`flex items-center gap-3 p-2.5 border border-gray-200 dark:border-gray-600 rounded-xl ${m.isActive ? '' : 'opacity-60'}`}>
                      <div className="w-52 shrink-0 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-mono text-sm font-medium text-gray-900 dark:text-white">{m.code}</span>
                          {m.origine === 'DECOUVERT' && <Badge color={ORIGINES.DECOUVERT.color}>Découvert</Badge>}
                          {!m.isActive && <Badge>Inactif</Badge>}
                        </div>
                        {m.libelle && <p className="text-xs text-gray-500 truncate">{m.libelle}</p>}
                      </div>
                      <input value={m.responsable || ''} onChange={(e) => setResponsable(m.code, e.target.value)} placeholder={`Responsable du module ${m.code}`} className={`${inputCls} flex-1`} disabled={!m.isActive} />
                      {m.isActive && (
                        <button onClick={() => retirerModule(m.code)} title="Retirer (le module sera désactivé, pas supprimé)" className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 shrink-0"><X className="h-4 w-4" /></button>
                      )}
                    </div>
                  ))}
                  {form.moduleDetails.length === 0 && <p className="text-sm text-gray-400 italic">Aucun module — ajoutez au moins un module à auditer</p>}
                </div>
                <p className="mt-1.5 text-xs text-gray-500">Le retrait d'un module le désactive côté serveur (aucune suppression : l'historique d'audit du module est conservé). Responsable optionnel par module.</p>
              </div>

              <label className="flex items-center gap-2 mt-4 text-sm text-gray-700 dark:text-gray-300">
                <input type="checkbox" checked={form.isActive} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))} className="h-4 w-4" />
                Domaine actif
              </label>
              <div className="flex justify-end gap-2 mt-5">
                <Button variant="ghost" onClick={() => setMode('detail')}>Annuler</Button>
                <Button onClick={submitForm} disabled={!formValide || saving}><Save className="h-4 w-4" />{saving ? 'Enregistrement…' : 'Enregistrer'}</Button>
              </div>
              {!formValide && <p className="text-xs text-gray-400 mt-2">Champs requis : Code (MAJUSCULES/chiffres/_), Nom et au moins un module actif.</p>}
            </div>
          ) : selected ? (
            <div>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  <span className="h-12 w-12 rounded-xl bg-indigo-500 flex items-center justify-center shrink-0"><Database className="h-6 w-6 text-white" /></span>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">{selected.name}</h3>
                    <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                      <span className="font-mono">{selected.code}</span>
                      <Badge color={ENV_COLORS[selected.environment]}>{selected.environment}</Badge>
                      <ActifBadge active={selected.isActive} />
                    </div>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => openEdit(selected)}><Edit className="h-4 w-4" />Modifier</Button>
              </div>

              <Tabs tabs={[{ id: 'info', label: 'Informations' }, { id: 'modules', label: 'Modules CBS', count: (selected.moduleDetails || []).length }]} active={tab} onChange={setTab} className="mb-4" />

              {tab === 'info' ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-3">
                      {selected.isActive ? <CheckCircle className="h-5 w-5 text-emerald-500" /> : <XCircle className="h-5 w-5 text-gray-400" />}
                      <div><div className="text-sm font-medium text-gray-900 dark:text-white">État du domaine</div><div className="text-xs text-gray-500 dark:text-gray-400">{selected.isActive ? 'Actif — audité par le pipeline' : 'Inactif — plus proposé pour de nouveaux audits'}</div></div>
                    </div>
                    {selected.isActive
                      ? <Button variant="ghost" size="sm" onClick={() => toggleDomainActive(selected)} className="text-red-600"><PowerOff className="h-4 w-4" />Désactiver</Button>
                      : <Button variant="ghost" size="sm" onClick={() => toggleDomainActive(selected)} className="text-emerald-600"><Power className="h-4 w-4" />Activer</Button>}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <StatCard icon={Server} label="Type de système" value={selected.systemType} />
                    <StatCard icon={Globe} label="Environnement" value={selected.environment} />
                    <StatCard icon={Tag} label="Version" value={selected.version} />
                    <StatCard icon={User} label="Responsable" value={selected.owner} />
                  </div>
                  <div className="rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700/60">
                    {[[Info, 'Description', selected.description], [CalendarDays, 'Créé le', `${selected.createdDate} par ${selected.createdBy}`], [CalendarDays, 'Dernière modification', `${selected.modifiedDate} par ${selected.modifiedBy}`]].map(([Ic, l, v], i) => (
                      <div key={i} className="flex items-center justify-between gap-4 px-3 py-2 text-sm">
                        <span className="flex items-center gap-2 text-gray-500 dark:text-gray-400 shrink-0"><Ic className="h-4 w-4" />{l}</span>
                        <span className="text-gray-800 dark:text-gray-200 text-right">{v || 'N/A'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-1.5">
                  {(selected.moduleDetails || []).map((m) => (
                    <div key={m.code} className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-700/50 text-sm">
                      <div className="flex items-center gap-2 min-w-0 flex-wrap">
                        <span className="font-mono font-medium text-gray-700 dark:text-gray-300">{m.code}</span>
                        {m.libelle && <span className="text-xs text-gray-500 truncate">{m.libelle}</span>}
                        <Badge color={ORIGINES[m.origine]?.color}>{ORIGINES[m.origine]?.label || m.origine}</Badge>
                        {!m.isActive && <Badge>Inactif</Badge>}
                      </div>
                      <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400 shrink-0"><Users className="h-4 w-4 text-gray-400" />{m.responsable || '—'}</span>
                    </div>
                  ))}
                  {(selected.moduleDetails || []).length === 0 && <p className="text-sm text-gray-400 italic">Aucun module déclaré pour ce domaine</p>}
                  <p className="text-xs text-gray-400 pt-2 flex items-center gap-1"><Layers className="h-3 w-3" />Ajout et retrait des modules via <strong>Modifier</strong> — le retrait désactive le module (référentiel AuditModule, unicité domaine + code).</p>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center h-32 text-gray-400">Sélectionnez un domaine dans la liste</div>
          )}
        </div>
      </Card>

      {/* ===== Guide utilisateur ===== */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Guide utilisateur</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Plus className="h-5 w-5 text-indigo-500" />Créer un domaine</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Cliquer sur <strong>« Nouveau Domaine »</strong> (en haut à droite). <button onClick={openCreate} className="text-indigo-600 dark:text-indigo-400 underline">Essayer</button></GuideStep>
              <GuideStep n={2}>Saisir le <strong>Code</strong> (majuscules, chiffres, underscores — unique et immuable après création) et le <strong>Nom</strong>.</GuideStep>
              <GuideStep n={3}>Choisir le <strong>Type de Système</strong> (CBS, CORE_BANKING, ERP…) et l'<strong>Environnement</strong> (PROD, UAT…).</GuideStep>
              <GuideStep n={4}>Ajouter <strong>au moins un module</strong> à auditer (code normalisé en MAJUSCULES).</GuideStep>
              <GuideStep n={5}>Laisser <strong>« Domaine actif »</strong> coché puis <strong>Enregistrer</strong>.</GuideStep>
            </ol>
          </CardContent></Card>

          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Layers className="h-5 w-5 text-indigo-500" />Gérer les modules CBS</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Sélectionner un domaine puis ouvrir l'onglet <strong>Modules CBS</strong>. <button onClick={() => { setMode('detail'); setTab('modules'); }} className="text-indigo-600 dark:text-indigo-400 underline">Ouvrir</button></GuideStep>
              <GuideStep n={2}>Chaque module porte un badge d'origine : <strong>Déclaré</strong> (saisi) ou <strong>Découvert</strong> (auto-provisionné à l'ingestion d'un XML).</GuideStep>
              <GuideStep n={3}>Cliquer sur <strong>Modifier</strong> pour ajouter un module par code (suggestions du référentiel + saisie libre).</GuideStep>
              <GuideStep n={4}>Affecter un <strong>responsable</strong> par module (optionnel — gouvernance).</GuideStep>
              <GuideStep n={5}><strong>Retirer un module = le désactiver</strong> : il reste au référentiel avec le badge « Inactif », jamais supprimé.</GuideStep>
            </ol>
          </CardContent></Card>

          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Power className="h-5 w-5 text-indigo-500" />Activer / Désactiver</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Depuis la liste, utiliser les boutons <strong>Activer / Désactiver</strong> de la colonne Actions.</GuideStep>
              <GuideStep n={2}>Ou depuis le détail : onglet Informations → bloc <strong>« État du domaine »</strong>.</GuideStep>
              <GuideStep n={3}>Un domaine <strong>inactif</strong> n'est plus proposé pour les nouveaux audits ; son historique reste consultable.</GuideStep>
              <GuideStep n={4}>La suppression définitive (DELETE) est réservée aux domaines sans historique — préférer la désactivation.</GuideStep>
            </ol>
          </CardContent></Card>
        </div>
      </div>

      {/* ===== Référence des champs ===== */}
      <Card><CardContent>
        <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Info className="h-5 w-5 text-indigo-500" />Référence des champs du formulaire</h3>
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
              <tr>{['Champ', 'Type', 'Requis', 'Détails'].map((h) => <th key={h} className="text-left font-semibold px-4 py-2">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-200">
              {[
                ['Code', 'Texte', 'Oui', 'Majuscules/chiffres/underscores, unique, immuable après création (clé du domaine)'],
                ['Nom', 'Texte', 'Oui', 'Libellé métier du système audité (200 car. max)'],
                ['Type de Système', 'Liste', 'Oui', 'CBS, CORE_BANKING, ERP, RISK_MANAGEMENT, AML, OTHER'],
                ['Environnement', 'Liste', 'Oui', 'PROD, UAT, DEV, PREPROD, DR'],
                ['Version', 'Texte', 'Non', 'Version du système audité (ex. v11, v2.5.1)'],
                ['Responsable', 'Utilisateur', 'Non', 'Username du responsable du domaine (sélecteur utilisateur)'],
                ['Description', 'Texte long', 'Non', 'Description libre (1000 car. max)'],
                ['Modules à auditer', 'Liste de codes', 'Oui', 'Au moins un module actif ; code normalisé trim().toUpperCase() ; responsable optionnel par module'],
                ['Domaine actif', 'Case à cocher', 'Non', 'Actif par défaut'],
              ].map((r, i) => (
                <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-4 py-2 font-medium">{r[0]}</td><td className="px-4 py-2">{r[1]}</td>
                  <td className="px-4 py-2">{r[2] === 'Oui' ? <Badge color="#EF4444">Oui</Badge> : <Badge>Non</Badge>}</td>
                  <td className="px-4 py-2 text-gray-600 dark:text-gray-300">{r[3]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent></Card>

      {/* ===== Règles de gestion + API ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card><CardContent>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Règles de gestion</h3>
          <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Le <strong>code</strong> du domaine est unique et immuable ; les codes de modules sont normalisés <code>trim().toUpperCase()</code> (unicité domaine + code).</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />L'enregistrement envoie <strong>modules[] + moduleOwners{'{}'}</strong> : le backend fait l'upsert vers le référentiel AuditModule (nouveau code → création <strong>DECLARE</strong> actif).</li>
            <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />Le <strong>retrait d'un module = désactivation, jamais suppression</strong> : l'historique des sessions et contrôles qui le référencent est conservé.</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Origine <strong>DECOUVERT</strong> : module auto-provisionné à l'ingestion d'un fichier XML (<code>scanbank.audit.auto-provisioning=true</code>).</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />À l'ingestion, le module du fichier est validé contre le référentiel selon <code>scanbank.module.validation</code> : OFF, WARN (défaut) ou STRICT.</li>
            <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />En modification d'un module, <strong>code et domaine sont immuables</strong> ; seuls libellé, description, responsable et statut évoluent.</li>
          </ul>
        </CardContent></Card>

        <Card><CardContent>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Endpoints API</h3>
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                <tr>{['Méthode', 'Endpoint', 'Permission'].map((h) => <th key={h} className="text-left font-semibold px-3 py-2">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-200 font-mono">
                {[
                  ['GET', '#10B981', '/api/scanbank/audit/domains/page?page,size,sort,search,systemType,environment,isActive', 'SCANBANK_AUDIT_VIEW'],
                  ['GET', '#10B981', '/api/scanbank/audit/domains/{code}', 'SCANBANK_AUDIT_VIEW'],
                  ['POST', '#3B82F6', '/api/scanbank/audit/domains', 'SCANBANK_AUDIT_CREATE'],
                  ['PUT', '#F59E0B', '/api/scanbank/audit/domains/{code}', 'SCANBANK_AUDIT_UPDATE'],
                  ['POST', '#3B82F6', '/api/scanbank/audit/domains/{code}/activate | /deactivate', 'SCANBANK_AUDIT_UPDATE'],
                  ['DELETE', '#EF4444', '/api/scanbank/audit/domains/{code}', 'SCANBANK_AUDIT_DELETE'],
                  ['GET', '#10B981', '/api/scanbank/audit/modules?domainCode=&active=', 'SCANBANK_AUDIT_VIEW'],
                  ['POST', '#3B82F6', '/api/scanbank/audit/modules', 'SCANBANK_AUDIT_CREATE'],
                  ['PUT', '#F59E0B', '/api/scanbank/audit/modules/{id}', 'SCANBANK_AUDIT_UPDATE'],
                  ['POST', '#3B82F6', '/api/scanbank/audit/modules/{id}/activate | /deactivate', 'SCANBANK_AUDIT_UPDATE'],
                ].map((r, i) => (
                  <tr key={i}>
                    <td className="px-3 py-1.5"><Badge color={r[1]}>{r[0]}</Badge></td>
                    <td className="px-3 py-1.5 break-all">{r[2]}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap">{r[3]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-400 mt-3 flex items-center gap-1"><ArrowRight className="h-3 w-3" />Source : scanbank_core / AuditScanBankController + AuditModuleController</p>
        </CardContent></Card>
      </div>
    </div>
  );
};

export default DomainesPOC;
